/* eslint-disable complexity */
import Cookies from 'js-cookie';
import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Banner} from './Banner/Banner';
import {BannerV2} from './BannerV2/BannerV2';
import {BannerModal} from './Modal/Modal';
import {EmbeddedComponent} from './EmbeddedComponent/EmbeddedComponent';
import {getBrand, RATING_TEXT, appBannerCtaLinks, appModalCtaLinks, appButtonCtaLinks, RATINGS, STARS} from './utils/bannerHelpers';
import {getExperimentValue, renderExperiments} from './utils/experiment';
import {I18n, readTransalationsBundles} from './utils/translation';
import {initBranch} from './utils/branch';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {handleHiddenEdapEvent} from './utils/triggerEdapEvent';
import {AppDownloadButton} from './AppDownloadButton/AppDownloadButton';

function AppBanner({locale, mobileOS, page, position}) {
    const renderNothing = () => {
        return (<Fragment />);
    };

    const fullLocale = locale ? locale : 'en_US_VRBO';
    locale = locale ? locale.match(/^.{2}_.{2}/)[0] : 'en_US';
    mobileOS = mobileOS || 'ios';

    const appBrandName = getBrand(fullLocale, locale);
    const travelerApp = appBrandName.toLowerCase();
    const getCtaLinks = (displayType) => {
        switch (displayType) {
            case 'banner' :
                return appBannerCtaLinks[travelerApp][page];
            case 'modal' :
                return appModalCtaLinks[travelerApp][page];
            case 'embedded':
                return appModalCtaLinks[travelerApp][`${page}-${position}`];
            case 'button':
                return appButtonCtaLinks[travelerApp][page];
            default :
                return '';
        }
    };
    const bannerCtaLink = (travelerApp && page) ? getCtaLinks('banner') : null;
    const embeddedCtaLink = (travelerApp && page) ? getCtaLinks('embedded') : null;
    const modalCtaLink = (travelerApp && page) ? getCtaLinks('modal') : null;
    const appDownloadButtonCtaLink = (travelerApp && page) ? getCtaLinks('button') : null;

    const useCanonicalPath = (page && page.toLowerCase() === 'serp') ? true : false;

    let props = {
        travelerApp,
        useCanonicalPath
    };

    const [headerExpVariant, setHeaderVariant] = useState(0);
    const [switchExpVariant, setSwitchVariant] = useState(1);
    const [labels, setLabels] = useState({});
    const [branchInfo, setBranchInfo] = useState({});
    const [componentType, setComponentType] = useState({});

    const context = useContext(TrackingContext);

    const getLabels = async () => {
        await I18n.load(locale);
        const labelObj = page && page !== 'harness' ? readTransalationsBundles(page, {brand: appBrandName}) : {};
        labelObj.ratings = labelObj.ratings || RATING_TEXT;
        const mobileOsRating = RATINGS[`${mobileOS}`];
        labelObj.rating = `${mobileOsRating} ${labelObj.ratings}`;
        labelObj.stars = STARS[`${mobileOS}`];
        setLabels(labelObj);
    };
    const euSiteCookie = Cookies.get('eu-site');
    const gdprCookie = Cookies.get('ha-cookie-settings'); // 'ha-cookie-settings' is a GDPR cookie used to set the consent for PII by EU user.

    const hasGdprClearance = () => {
        return !(euSiteCookie === '1' && gdprCookie === 'functional:1'); // 'functional:0' is the consent for PII
    };

    const isGDPRBannerHidden = () => {
        return (!euSiteCookie || euSiteCookie === '0' || gdprCookie);
    };

    const getExperimentVariant = () => {
        const displayExpName = renderExperiments[page];
        return (displayExpName ? getExperimentValue(displayExpName) : -1);
    };

    const getComponentType = () => {
        const displayExpVariant = getExperimentVariant();
        let componentTypeInfo = 'others';
        switch (page) {
            case 'mytrips':
                componentTypeInfo = 'modal';
                break;
            case 'confirmation':
                if ((displayExpVariant === 1 && !position) || displayExpVariant === -1) {
                    componentTypeInfo = 'modal';
                } else if ((displayExpVariant === 2 && position === 'above-share-trip') || (displayExpVariant === 3 && position === 'below-share-trip')) {
                    componentTypeInfo = 'embedded';
                }
                break;
            case 'serp':
                if (displayExpVariant === 1 || displayExpVariant === -1) {
                    componentTypeInfo = 'banner';
                }
                break;
            case 'homepage':
                if (displayExpVariant === -1 || displayExpVariant === 1) {
                    componentTypeInfo = 'banner';
                } else if (displayExpVariant === 2) {
                    componentTypeInfo = 'modal';
                }
                break;
            case 'inbox':
                if (displayExpVariant === 1 || displayExpVariant === -1) {
                    componentTypeInfo = 'banner';
                }
                break;
            case 'header':
                componentTypeInfo = 'button';
                break;
            default:
                componentTypeInfo = 'others';
        }
        return componentTypeInfo;
    };

    const setBranchAndComponentTypeInfo = () => {
        const componentTypeInfo = getComponentType();
        setComponentType(componentTypeInfo);
        const hasAppExpVariant = getExperimentValue('HA_NATIVE_ADOPTION_BANNER_USE_HAS_APP');
        branchInfo.branchInitializationRequired = (componentTypeInfo !== 'others' && hasAppExpVariant === 1 && hasGdprClearance());
        setBranchInfo(branchInfo);
    };

    const setComponentTypeAndInitilizeBranchSDK = () => {
        setBranchAndComponentTypeInfo();
        if (branchInfo.branchInitializationRequired && !branchInfo.hasApp) {
            try {
                initBranch((err, data) => {
                    const hasApp = (data && data.has_app) ? 'true' : 'false';
                    branchInfo.hasApp = hasApp;
                    setBranchInfo({...branchInfo, branchInfo});
                }, false);
            } catch (err) {
                branchInfo.hasApp = 'false';
                setBranchInfo({...branchInfo, branchInfo});
            }
        }
    };

    useEffect(() => {
        const switchExp = getExperimentValue('HA_NATIVE_ADOPTION_PLACEMENTS_MASTER_SWITCH');
        setSwitchVariant(switchExp);
        const headerExp = getExperimentValue('HA_NATIVE_ADOPTION_PLACEMENT_HEADER');
        setHeaderVariant(headerExp);
    }, [page]);

    useEffect(() => {
        setComponentTypeAndInitilizeBranchSDK();
        getLabels();
    }, []);

    const renderBanner = () => {
        const bannerExp = getExperimentValue('HA_NATIVE_ADOPTION_BANNER_V2');
        if (bannerExp === 0 || page === 'harness') {
            return (<Banner ctaLink={bannerCtaLink} {...labels} {...props}/>);
        }
        return (<BannerV2 ctaLink={bannerCtaLink} {...labels} {...props}/>);
    };

    const renderModal = () => {
        return (<BannerModal ctaLink={modalCtaLink} {...labels} {...props}/>);
    };

    const renderEmbeddedComponent = () => {
        return (<EmbeddedComponent ctaLink={embeddedCtaLink} {...labels} {...props}/>);
    };

    const renderButtonComponent = () => {
        return (<AppDownloadButton ctaLink={appDownloadButtonCtaLink} {...labels} {...props}/>);
    };

    const renderMyTrips = () => {
        return (componentType === 'modal' ? renderModal() : renderNothing());
    };
    const renderInbox = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderConfirmation = () => {
        if (componentType === 'modal') {
            return renderModal();
        } else if (componentType === 'embedded') {
            return renderEmbeddedComponent();
        }
        return renderNothing();
    };
    const renderSerp = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderHomePage = () => {
        switch (componentType) {
            case 'banner':
                return renderBanner();
            case 'modal':
                labels.description = labels.modal_description;
                labels.title = labels.modal_title;
                labels.buttonLabel = labels.modal_buttonLabel;
                return renderModal();
            default:
                return renderNothing();
        }
    };
    const renderHeader = () => {
        return (componentType === 'button' ? renderButtonComponent() : renderNothing());
    };
    const triggerHasAppEdapEvent = () => {
        if (branchInfo.hasApp === 'true') {
            handleHiddenEdapEvent(context, componentType);
        }
    };

    const mobAppInstalled = () => {
        return branchInfo.branchInitializationRequired && (!branchInfo.hasApp || branchInfo.hasApp === 'true');
    };

    const appBanner = () => {
        if (switchExpVariant === 1 && page !== 'harness') {
            return renderNothing();
        }
        if ((headerExpVariant === 1 && page !== 'header') || (headerExpVariant !== 1 && page === 'header')) {
            // 1.Header Exp is on but AppBanner call comes from HP/SERP/STUI
            // 2.AppBanner call comes from header but Header Exp is off
            return renderNothing();
        }
        if (mobAppInstalled()) {
            triggerHasAppEdapEvent();
            return renderNothing();
        }
        if (!isGDPRBannerHidden() && (componentType !== 'embedded' || componentType !== 'button')) {
            return renderNothing();
        }
        switch (page) {
            case 'mytrips':
                return renderMyTrips();
            case 'confirmation':
                return renderConfirmation();
            case 'serp':
                return renderSerp();
            case 'homepage':
                return renderHomePage();
            case 'inbox':
                return renderInbox();
            case 'header':
                return renderHeader();
            case 'harness':
                return renderBanner();
            default: return renderNothing();
        }
    };
    return appBanner();
}

export default AppBanner;
